/**
 * Flowing-ink canvas background for the login page.
 *
 * A handful of large, soft radial "ink blooms" drift + breathe over the
 * theme's --canvas ground, with the occasional emerald wisp — echoing the
 * Folio favicon's layered translucent sheets (#161D29 ground, off-white
 * blooms, #10C083 spark). Dependency-free 2D canvas, no framework.
 *
 * Theme-aware: ground = --canvas, blooms = --ink (which resolves LIGHT in
 * dark mode and DARK in light mode, so blooms always read as "ink" over the
 * ground), spark = --brand-primary. Palette is sampled once at start.
 *
 * Cheap by design: ~7 blooms, DPR capped, ~30fps, each bloom fills only its
 * own bounding box, and the loop pauses while the tab is hidden. Honours
 * prefers-reduced-motion by painting a single static frame (no loop).
 *
 * Returns a cleanup function that stops the loop + detaches observers.
 */

interface Bloom {
  x: number; // normalised centre (can drift slightly off [0,1], then bounces)
  y: number;
  r: number; // base radius as a fraction of the canvas diagonal
  vx: number; // drift velocity (normalised units / second)
  vy: number;
  phase: number; // breathing phase offset
  speed: number; // breathing speed
  color: string; // resolved base colour (oklch/hex/rgb — any CSS colour)
  alpha: number; // peak centre alpha
}

const MAX_DPR = 1.5;
const FRAME_MS = 1000 / 30; // ~30fps throttle — motion is slow, 60 is wasteful

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Resolve a CSS custom property to a usable colour off an element. */
function tokenColor(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/** Wrap any CSS colour with an alpha via color-mix (works for oklch/hex/rgb). */
function withAlpha(color: string, a: number): string {
  return `color-mix(in srgb, ${color} ${Math.round(a * 100)}%, transparent)`;
}

export function startInkCanvas(canvas: HTMLCanvasElement, paletteHost: Element): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const reduced = reducedMotion();

  const ground = tokenColor(paletteHost, '--canvas', '#161D29');
  const ink = tokenColor(paletteHost, '--ink', '#FDFCFA');
  const spark = tokenColor(paletteHost, '--brand-primary', '#10C083');

  // Deterministic seeding (no Math.random): positions/speeds vary by index so
  // the field looks organic but renders identically on every load.
  const seeds: Array<[number, number]> = [
    [0.20, 0.24], [0.78, 0.18], [0.62, 0.72], [0.32, 0.82], [0.50, 0.46], [0.88, 0.60],
  ];
  const blooms: Bloom[] = seeds.map(([x, y], i) => ({
    x, y,
    r: 0.34 + (i % 3) * 0.06,
    vx: (i % 2 ? 1 : -1) * (0.006 + (i % 3) * 0.002),
    vy: (i % 3 ? -1 : 1) * (0.005 + (i % 2) * 0.002),
    phase: i * 1.1,
    speed: 0.12 + (i % 4) * 0.03,
    color: ink,
    alpha: 0.12 + (i % 3) * 0.04,
  }));
  // One slow emerald wisp for the Folio accent spark.
  blooms.push({ x: 0.68, y: 0.30, r: 0.24, vx: 0.004, vy: 0.0035, phase: 0.5, speed: 0.10, color: spark, alpha: 0.13 });

  let w = 0, h = 0, diag = 1;
  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    w = Math.max(1, rect.width);
    h = Math.max(1, rect.height);
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    diag = Math.hypot(w, h);
  }

  function draw(t: number): void {
    ctx!.globalCompositeOperation = 'source-over';
    ctx!.fillStyle = ground;
    ctx!.fillRect(0, 0, w, h);
    for (const b of blooms) {
      const breathe = 1 + 0.18 * Math.sin(b.phase + t * b.speed);
      const cx = b.x * w;
      const cy = b.y * h;
      const rad = b.r * diag * breathe;
      const g = ctx!.createRadialGradient(cx, cy, 0, cx, cy, rad);
      g.addColorStop(0, withAlpha(b.color, b.alpha));
      g.addColorStop(1, withAlpha(b.color, 0));
      ctx!.fillStyle = g;
      ctx!.fillRect(cx - rad, cy - rad, rad * 2, rad * 2); // bounding box only
    }
  }

  function step(dt: number): void {
    for (const b of blooms) {
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      // Bounce a little past the edges (no wrap → no teleport pop).
      if (b.x < -0.12 || b.x > 1.12) b.vx *= -1;
      if (b.y < -0.12 || b.y > 1.12) b.vy *= -1;
    }
  }

  resize();
  const ro = new ResizeObserver(() => {
    resize();
    if (reduced) draw(0);
  });
  ro.observe(canvas);

  if (reduced) {
    draw(0);
    return () => ro.disconnect();
  }

  let raf = 0;
  let prev = 0;
  let clock = 0;
  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    if (!prev) prev = now;
    const dtMs = now - prev;
    if (dtMs < FRAME_MS) return; // throttle to ~30fps
    prev = now;
    const dt = Math.min(0.1, dtMs / 1000); // clamp big gaps (e.g. tab return)
    clock += dt;
    step(dt);
    draw(clock);
  }
  function onVisibility(): void {
    if (document.hidden) {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
    } else if (!raf) {
      prev = 0;
      raf = requestAnimationFrame(frame);
    }
  }
  document.addEventListener('visibilitychange', onVisibility);
  raf = requestAnimationFrame(frame);

  return () => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    ro.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
